import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const ModernFarmhouseADUDesignBurlingame = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Modern Farmhouse ADU Design & Construction in Burlingame",
    "description": "Build beautiful Modern Farmhouse ADUs in Burlingame with barn-inspired details, shiplap siding, and contemporary comfort.",
    "url": "https://www.udaadu.com/modern-farmhouse-adu-design-burlingame",
    "mainEntity": {
      "@type": "Service",
      "name": "Modern Farmhouse ADU Design & Construction",
      "provider": {
        "@type": "Organization",
        "name": "UDA ADU"
      },
      "areaServed": "Burlingame, CA"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/modern-farmhouse-adu-design-burlingame" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Modern Farmhouse ADU Design & Construction in Burlingame
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Build beautiful Modern Farmhouse ADUs in Burlingame with barn-inspired details, shiplap siding, and contemporary comfort.
          </p>
        </div>

        <div className="text-center mb-16">
          <Button onClick={openCalendlyModal} size="lg" className="mr-4">
            Get Free Consultation
          </Button>
          <Button asChild variant="outline" size="lg" className="text-white border-white hover:bg-white hover:text-black">
            <Link to="/adu-calculator">Calculate Costs</Link>
          </Button>
        </div>

        {/* Why Choose Modern Farmhouse */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-white">Why Choose Modern Farmhouse ADUs in Burlingame?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-white">Rustic Elegance</h3>
              <p className="text-muted-foreground">
                Modern Farmhouse ADUs combine rustic charm with contemporary sophistication, perfect for Burlingame's upscale neighborhoods.
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-white">Market Appeal</h3>
              <p className="text-muted-foreground">
                This popular style attracts quality tenants and maintains strong property values in Burlingame's competitive rental market.
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-white">Comfortable Living</h3>
              <p className="text-muted-foreground">
                Open layouts and natural materials create warm, inviting spaces that residents love to call home.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-white">Modern Farmhouse ADU Features</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-white">Exterior Elements</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Mixed siding materials</li>
                <li>• Barn-style doors and hardware</li>
                <li>• Wraparound or covered porches</li>
                <li>• Black window frames</li>
                <li>• Metal or composite roofing</li>
              </ul>
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-white">Interior Design</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Shiplap and board-and-batten walls</li>
                <li>• Exposed ceiling beams</li>
                <li>• Farmhouse-style fixtures</li>
                <li>• Large kitchen islands</li>
                <li>• Built-in storage and seating</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Cost Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-white">Investment Range</h2>
          <div className="text-center p-8 border border-border rounded-lg">
            <p className="text-2xl font-bold mb-4 text-white">$280,000 - $440,000</p>
            <p className="text-muted-foreground">
              Modern Farmhouse ADUs provide excellent returns with their popular design and quality finishes that appeal to Burlingame residents.
            </p>
          </div>
        </section>

        {/* Final CTA */}
        <section className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-8 text-white">Start Your Modern Farmhouse ADU Project</h2>
          <p className="text-xl mb-8 text-muted-foreground max-w-3xl mx-auto">
            Ready to bring Modern Farmhouse style to your Burlingame property? Let's create your perfect blend of rustic charm and modern comfort.
          </p>
          <Button onClick={openCalendlyModal} size="lg" className="mr-4">
            Schedule Consultation
          </Button>
          <Button asChild variant="outline" size="lg" className="text-white border-white hover:bg-white hover:text-black">
            <Link to="/adu-calculator">Get Cost Estimate</Link>
          </Button>
        </section>
      </main>

      <FloatingADUWizard />
      <FloatingReserveButton />
      <Footer />
    </div>
  );
};

export default ModernFarmhouseADUDesignBurlingame;